/**
 * @file `ChapterPage` — `/courses/:courseId/:partId/:chapterId`.
 * @module @labcraft/lab/ui/pages/chapter
 *
 * SPA-side consumer of the URL-isomorphic combined chapter endpoint
 * (`GET /api/courses/:courseId/:partId/:chapterId`). The page reads
 * its URL, fetches `{kind:'ok'|'conflict', ...}` from lab, and
 * renders one of three surfaces:
 *
 *   - `'ok'` → mount `<ChapterProvider/>` with the spec from the
 *     response, lazy-load the MDX module via `CourseRegistry`, and
 *     attach `app.terminals` to the response's machine list. The
 *     mirrorable `<SplitLayout/>` holds the chapter prose on one side
 *     and a `<WorkspacePane/>` on the other; the workspace renders one
 *     tab per machine (live/idle dot + env label) over a single visible
 *     terminal, with every machine's pane kept mounted so its xterm +
 *     WS survive tab switches (#936).
 *   - `'conflict'` → render `<ConflictDialog/>` with the engaged-
 *     elsewhere summary; Continue navigates to `current.chapterUrl`,
 *     Start-fresh POSTs `/api/courses/engagement` and refetches.
 *   - error/loading → preparing-lab overlay; fatal-overlay on
 *     unrecoverable fetch failure.
 *
 * Eviction: `app.chapter.onClose` fires when any pooled WS closes;
 * if the close came from a server-initiated engagement switch (close
 * code 1000 from hypervisor.shutdown propagation), the page renders an
 * eviction takeover scoped to the terminal pane (the chapter stays
 * readable). "Wake it up" refetches at the current URL, which surfaces
 * the conflict dialog with the new engagement as `current`.
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type JSX,
} from 'react'
import { useNavigate, useParams } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import {
  ChapterProvider,
  type ChapterFocus,
  type CommandSource,
  type MentorSource,
  type ProbeSource,
  type ReaderFunnelEvent,
  type ReassertSource,
} from '@labcraft/course-kit/runtime'
import { track } from '@labcraft/ui/hooks'
import {
  ChapterFooter,
  ChapterHeader,
  ChapterProgress,
  Glossary,
  MentorDock,
  GlossaryProvider,
  MdxProvider,
  type ChapterNavLink,
} from '@labcraft/course-kit/components'
import {
  QuizSerde,
  reviveSpecWire,
  type ChapterModule,
  type ChapterSpec,
  type Term,
} from '@labcraft/course-kit'
import type { Lab } from '../../client/index.ts'
import type {
  ChapterResponse,
  PartFeedbackRequest,
  ReportContext,
} from '../../shared/routes.ts'
import {
  TERMINAL_CLOSE_CODES,
  type BudgetExhaustedPayload,
  type MachineLifecycleReason,
} from '../../shared/ws_messages.ts'
import {
  SplitLayout,
  readStoredOrientation,
  type SplitOrientation,
} from '../components/split_layout.tsx'
import { ChapterPane } from '../components/chapter_pane.tsx'
import { AtCapacityOverlay } from '../components/at_capacity_overlay.tsx'
import { ClosingFeedback } from '../components/closing_feedback.tsx'
import { OutOfTimeTakeover } from '../components/out_of_time_takeover.tsx'
import {
  WorkspacePane,
  type WorkspaceMachine,
} from '../components/workspace_pane.tsx'
import { useSetReaderChrome } from '../lib/reader_chrome.tsx'
import {
  BOOT_TROUBLE_COPY,
  bumpUpstreamFailure,
  isBootWedged,
  type UpstreamFailureTally,
} from '../lib/boot_trouble.ts'
import { useMe } from '../hooks/use_me.ts'
import { useSetChapterMutation } from '../hooks/use_set_chapter.ts'
import styles from './chapter.module.css'

/**
 * `useChapter` — fetch + cache the combined chapter response for the
 * current URL. Refires on URL change. Returns a discriminated `state`
 * that the page renders against, plus a `refetch` callback so the
 * eviction and Bring-it-here paths can re-hit the endpoint.
 *
 * Loading is the initial state and re-entered on every URL change;
 * on success, `state` becomes `'ok'` or `'conflict'` depending on the
 * server's discriminator. Fetch errors land in `'error'` with the
 * error message; the page surfaces a Retry control that calls
 * `refetch`.
 */
type FetcherState =
  | { state: 'loading' }
  | { state: 'ok'; response: Extract<ChapterResponse, { kind: 'ok' }> }
  | {
      state: 'conflict'
      response: Extract<ChapterResponse, { kind: 'conflict' }>
    }
  | { state: 'error'; error: string }

function useChapter(
  app: Lab,
  courseId: string,
  partId: string,
  chapterId: string,
): { state: FetcherState; refetch: () => void } {
  const [state, setState] = useState<FetcherState>({ state: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)
  const refetch = useCallback(() => setReloadKey(k => k + 1), [])

  useEffect(() => {
    if (!courseId || !partId || !chapterId) return
    let cancelled = false
    setState({ state: 'loading' })
    app.chapter
      .getChapter(courseId, partId, chapterId)
      .then(response => {
        if (cancelled) return
        if (response.kind === 'ok') {
          setState({ state: 'ok', response })
        } else {
          setState({ state: 'conflict', response })
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({
          state: 'error',
          error: err instanceof Error ? err.message : String(err),
        })
      })
    return () => {
      cancelled = true
    }
  }, [app, courseId, partId, chapterId, reloadKey])

  return { state, refetch }
}

export function ChapterPage(): JSX.Element | null {
  const navigate = useNavigate()
  const app = useClient<Lab>()
  const params = useParams()
  const courseId = params.courseId ?? ''
  const partId = params.partId ?? ''
  const chapterId = params.chapterId ?? ''

  const { state, refetch } = useChapter(app, courseId, partId, chapterId)
  const me = useMe()
  const setChapter = useSetChapterMutation()

  // Umbrella #1446 Part C, #1572 — release the engagement when the learner
  // leaves the lab via a real document unload. `pagehide` fires on tab close,
  // reload, or off-site navigation but NOT on SPA client-side route changes,
  // so intra-app chapter navigation never churns the engagement. This is an
  // INVOLUNTARY leave (the learner may well come back), so it releases with
  // `intent: 'suspend'` — the record is deleted but the VM is left to the
  // hypervisor's 2-minute disconnect-grace, which snapshots it to `saved` so
  // the learner resumes where they left off. Best-effort: a dropped beacon is
  // harmless (the conflict gate keys on live terminals, not the record).
  useEffect(() => {
    const onPageHide = (): void => {
      // `keepalive` so the browser finishes the DELETE after the document
      // unloads — a normal request would be cancelled mid-flight on close.
      void app.chapter
        .leaveEngagement({ keepalive: true, intent: 'suspend' })
        .catch(() => undefined)
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [app])

  // ADR-006 (g) — push the URL-derived chapter pointer to Postgres
  // so a learner returning after engagement-TTL expiry resumes here.
  // Best-effort; failures don't block render.
  useEffect(() => {
    if (!courseId || !partId || !chapterId) return
    const current = me.data?.learner?.currentChapter
    if (
      current?.courseId === courseId &&
      current?.partId === partId &&
      current?.chapterId === chapterId
    ) {
      return
    }
    setChapter.mutate(
      { courseId, partId, chapterId },
      {
        onError: (err: unknown) => {
          console.warn('setChapter failed', err)
        },
      },
    )
    // The mutation closure is stable per-render; the effect only
    // refires on URL changes or learner-state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, partId, chapterId, me.data?.learner?.currentChapter])

  // Eviction watch (#1056) — any pool member closing fires here. Each
  // machine gets its OWN eviction entry keyed by machineId (multi-tab
  // machine workspaces shipped; a blip on one machine must not evict the
  // whole pool) carrying the lifecycle `reason` so the takeover copy is
  // specific. One client-side reconnect is attempted before the takeover
  // shows — a transient blip heals silently; a persistent close surfaces
  // the deliberate recovery.
  const [evictions, setEvictions] = useState<
    Map<string, MachineLifecycleReason | null>
  >(new Map())
  // #1009: a terminally-`failed` machine (a snapshot write hung past the
  // watchdog) closes 4005 — distinct from the resumable codes below.
  // Resuming is impossible, so it gets its own "start a fresh shell"
  // takeover whose recovery is terminate + cold-boot, not re-attach.
  const [failedMachineId, setFailedMachineId] = useState<string | null>(null)
  // #1315: a spent free-tier daily cap. Set from the `budget-exhausted`
  // close (below) with the frame's upgrade payload; drives the calm
  // out-of-time takeover. Distinct from `evictions` because the wall is
  // NOT reconnectable — a resume would boot, hit the pre-open budget gate,
  // and terminate again (the flicker loop). Its only recovery is upgrade or
  // waiting for the reset, both of which the takeover offers.
  const [budgetExhausted, setBudgetExhausted] =
    useState<BudgetExhaustedPayload | null>(null)
  // The host was full and the learner was queued (umbrella #1257 Part E,
  // #1284): the terminal WS closes 4429 (AT_CAPACITY) and the SPA shows the
  // at-capacity / enable-notifications overlay. Persists until the learner
  // reloads (via the "your spot is ready" push) — the WS is closed, so there
  // is no live signal to auto-dismiss.
  const [atCapacity, setAtCapacity] = useState(false)
  // The learner's place in line, buffered from the `at-capacity` frame that
  // arrives just before the 4429 close (umbrella #1257 Part E, #1284). Undefined
  // when the frame didn't arrive before the close (an onData/onClose race) — the
  // overlay then omits the "you're #N in line" line.
  const [atCapacityPosition, setAtCapacityPosition] = useState<
    number | undefined
  >(undefined)
  // Machines that have already spent their one silent-reconnect attempt
  // this eviction cycle. Reset when a machine reconnects cleanly.
  const reconnectedOnce = useRef<Set<string>>(new Set())
  // #1593: machines whose boot keeps failing at the hypervisor hop
  // (UPSTREAM_FAILED / 4004) — the honest "trouble starting your shell" surface
  // replaces the misleading "dozed off" loop once the failures are persistent.
  const [bootTrouble, setBootTrouble] = useState<Set<string>>(new Set())
  // Per-machine consecutive 4004 tally with the last-failure timestamp, so a
  // rapid boot-fail loop trips the cap while spaced-out transient blips reset.
  const upstreamFail = useRef<Map<string, UpstreamFailureTally>>(new Map())
  useEffect(() => {
    return app.chapter.onClose((machineId, code, wsReason) => {
      const reason = app.chapter.terminalFor(machineId)?.lastReason ?? null
      console.debug('[chapter] terminal close', {
        machineId,
        code,
        wsReason,
        reason,
      })
      if (code === TERMINAL_CLOSE_CODES.AT_CAPACITY) {
        // Host full — the learner is queued. Surface the at-capacity /
        // enable-notifications overlay with their place in line (buffered from
        // the frame that landed just before this close); no silent reconnect
        // (there's no VM to reattach to yet). Cleared on reload when their spot
        // opens.
        setAtCapacityPosition(
          app.chapter.terminalFor(machineId)?.lastAtCapacity?.position,
        )
        setAtCapacity(true)
        return
      }
      if (code === TERMINAL_CLOSE_CODES.MACHINE_FAILED) {
        // Terminal failure wins over the resumable eviction surface —
        // a prior 1000/4004 may have evicted the machine on the way here
        // (live watchdog: pty-kill 1000 → reconnect → attach 4005).
        setEvictions(prev => {
          if (!prev.has(machineId)) return prev
          const next = new Map(prev)
          next.delete(machineId)
          return next
        })
        setFailedMachineId(machineId)
        return
      }
      if (code === TERMINAL_CLOSE_CODES.BUDGET_EXHAUSTED) {
        // #1315: a spent free-tier cap is NOT a reconnectable eviction. The
        // signal is the 4403 CLOSE CODE, not the lifecycle `reason` (which is
        // `null` on a budget close) — keying on `reason` misses it entirely.
        // The `budget-exhausted` frame that preceded this close buffered the
        // upgrade URL; surface the calm out-of-time takeover from it and
        // neither reattach nor arm the resumable "Wake it up" eviction
        // surface. Recovery is upgrade or the daily reset. (The transport
        // itself no longer reconnects on 4403 — it's a fatal close code per
        // `WsSubscription.isFatalCloseCode` — so this is the sole surface.)
        setBudgetExhausted(
          app.chapter.terminalFor(machineId)?.lastBudgetExhausted ?? null,
        )
        return
      }
      if (code === TERMINAL_CLOSE_CODES.SUSPENDED) {
        // #1083: a park-suspend (voluntary Pause / idle-suspend /
        // operator-suspend) snapshotted a RESUMABLE VM. Do NOT spend the
        // silent-reconnect budget — a re-attach restores the snapshot and
        // undoes the park. Surface the reason-specific parked takeover with
        // an explicit Resume straight away; the `reason` (best-effort from
        // the lifecycle frame) drives the copy, falling back to the generic
        // "dozed off" surface if it didn't arrive before the close.
        setEvictions(prev => new Map(prev).set(machineId, reason))
        return
      }
      if (code === TERMINAL_CLOSE_CODES.BOOT_RETRYABLE) {
        // #1602: the hypervisor CLASSIFIED this as a retryable boot-infra fault
        // (cgroup / network / image / restore / spawn) — a server-side failure
        // to start the shell, not a transient blip. Surface the honest "trouble
        // starting your shell" takeover IMMEDIATELY; no need to count three
        // 4004s first. The UPSTREAM_FAILED count heuristic below stays as the
        // fallback for a generic upstream drop the hypervisor couldn't classify.
        setBootTrouble(prev => new Set(prev).add(machineId))
        return
      }
      if (code === TERMINAL_CLOSE_CODES.UPSTREAM_FAILED) {
        // #1593: the hypervisor hop failed to OPEN. A one-off is a transient
        // blip (fall through to the single silent reconnect); a rapid RUN of
        // them is a persistent server-side boot failure — surface the honest
        // "trouble starting your shell" recovery instead of looping "dozed off
        // / Wake it up" forever. Consecutive closes within the window
        // accumulate; a gap longer than the window resets the tally.
        const tally = bumpUpstreamFailure(
          upstreamFail.current.get(machineId),
          Date.now(),
        )
        upstreamFail.current.set(machineId, tally)
        if (isBootWedged(tally)) {
          setBootTrouble(prev => new Set(prev).add(machineId))
          return
        }
        // Below the cap — fall through to the resumable path so a genuine
        // transient blip still heals via the one silent reconnect.
      }
      // Every other close (app-level 4001-4004, or a 1000 shutdown
      // propagation) is resumable. Try one silent reconnect before the
      // takeover — a slept tab / WiFi flicker heals without the learner
      // ever seeing a dialog. The budget of one retry per machine is
      // refilled when the learner clicks Resume (`onResume` below).
      const terminal = app.chapter.terminalFor(machineId)
      if (terminal && !reconnectedOnce.current.has(machineId)) {
        reconnectedOnce.current.add(machineId)
        terminal.reattach()
        return
      }
      // The reconnect didn't stick — surface the reason-specific
      // takeover for this machine.
      setEvictions(prev => new Map(prev).set(machineId, reason))
    })
  }, [app])

  // Bounce empty URLs (router shouldn't allow this, but defend).
  useEffect(() => {
    if (!courseId || !partId || !chapterId) {
      navigate('/courses', { replace: true })
    }
  }, [courseId, partId, chapterId, navigate])

  if (state.state === 'loading') {
    return <PreparingLab />
  }

  if (state.state === 'error') {
    return <FatalOverlay message={state.error} onRetry={refetch} />
  }

  if (state.state === 'conflict') {
    return (
      <ConflictDialog
        current={state.response.current}
        onContinue={() => navigate(state.response.current.chapterUrl)}
        onStartFresh={async () => {
          try {
            await app.chapter.postEngagement(
              `/courses/${courseId}/${partId}/${chapterId}`,
            )
            refetch()
          } catch (err) {
            console.error('postEngagement failed', err)
            // Fall through — refetch may still recover.
            refetch()
          }
        }}
      />
    )
  }

  return (
    <>
      {atCapacity && <AtCapacityOverlay position={atCapacityPosition} />}
      <OkChapterView
        response={state.response}
        evictions={evictions}
        budgetExhausted={budgetExhausted}
        onResume={machineId => {
          // Refill the machine's silent-reconnect budget and clear its
          // eviction; the refetch re-attaches the pool.
          reconnectedOnce.current.delete(machineId)
          setEvictions(prev => {
            const next = new Map(prev)
            next.delete(machineId)
            return next
          })
          refetch()
        }}
        onExpiredRestart={async () => {
          // engagement-expired recovery (#1056): re-post the engagement at
          // the current URL, then re-fetch. Distinct from Resume — the
          // engagement lapsed, so there's no snapshot to wake; a fresh
          // engagement is minted.
          try {
            await app.chapter.postEngagement(
              `/courses/${courseId}/${partId}/${chapterId}`,
            )
          } catch (err) {
            console.error('onExpiredRestart postEngagement failed', err)
          }
          reconnectedOnce.current.clear()
          upstreamFail.current.clear()
          setEvictions(new Map())
          setBudgetExhausted(null)
          setBootTrouble(new Set())
          refetch()
        }}
        bootTrouble={bootTrouble}
        onBootRetry={machineId => {
          // #1593: an explicit retry from the "trouble starting" surface —
          // give the boot a fresh window (clears the 4004 tally + the
          // silent-reconnect budget) and re-attach. If it's still wedged the
          // tally re-accumulates and the honest surface returns.
          upstreamFail.current.delete(machineId)
          reconnectedOnce.current.delete(machineId)
          setBootTrouble(prev => {
            const next = new Set(prev)
            next.delete(machineId)
            return next
          })
          setEvictions(prev => {
            const next = new Map(prev)
            next.delete(machineId)
            return next
          })
          refetch()
        }}
        failedMachineId={failedMachineId}
        onRecovered={() => setFailedMachineId(null)}
      />
    </>
  )
}

interface OkChapterViewProps {
  response: Extract<ChapterResponse, { kind: 'ok' }>
  evictions: Map<string, MachineLifecycleReason | null>
  budgetExhausted: BudgetExhaustedPayload | null
  onResume: (machineId: string) => void
  onExpiredRestart: () => void | Promise<void>
  bootTrouble: Set<string>
  onBootRetry: (machineId: string) => void
  failedMachineId: string | null
  onRecovered: () => void
}

function OkChapterView(props: OkChapterViewProps): JSX.Element {
  const {
    response,
    evictions,
    budgetExhausted,
    onResume,
    onExpiredRestart,
    bootTrouble,
    onBootRetry,
    failedMachineId,
    onRecovered,
  } = props
  const navigate = useNavigate()
  const app = useClient<Lab>()
  const [Module, setModule] = useState<ComponentType | null>(null)
  const [moduleError, setModuleError] = useState<string | null>(null)
  const [moduleReloadKey, setModuleReloadKey] = useState(0)

  // Split orientation — the live source of truth for the reader layout,
  // seeded from the persisted value so a refresh restores the learner's
  // last layout. This state is published up to the app bar via
  // `ReaderChromeContext`, where the Settings "Default layout" control
  // sets it; `SplitLayout` renders from it and persists every change to
  // `labcraft.splitOrientation` (see split_layout.tsx).
  const [orientation, setOrientation] = useState<SplitOrientation>(() =>
    readStoredOrientation(),
  )

  // Attach / re-attach the pool whenever the machine list changes,
  // and project the first-machine `Terminal` into React state so the
  // render reacts to the post-attach pool state. The pool itself is a
  // mutable Client — mutations don't trigger re-renders on their own,
  // so without this projection, the component reads from an empty
  // pool on first render and stays stuck. The pool's `attach` is a
  // diff: matching machineIds with unchanged terminalIds are no-ops,
  // so within-Part chapter nav with the same recipe is WS-free.
  const [primaryTerminal, setPrimaryTerminal] =
    useState<ReturnType<Lab['chapter']['terminalFor']>>(undefined)
  useEffect(() => {
    app.chapter.attachTerminals(response.machines)
    const firstId = response.machines[0]?.machineId
    setPrimaryTerminal(firstId ? app.chapter.terminalFor(firstId) : undefined)
  }, [app, response.machines])

  // Tear down the pool on page unmount (e.g. learner navigates to
  // a different route entirely). Within-page URL changes are
  // handled by the re-fetch above.
  useEffect(() => {
    return () => {
      app.chapter.detachTerminals()
    }
  }, [app])

  // Adapt the primary terminal's command-event feed (#627/#628 OSC 133
  // → GateSub → lab `command` frames) into a course-kit CommandSource so
  // `<ChapterProvider>` advances objectives on what the learner types
  // (#629). Stable across renders (keyed on the primary terminal) so the
  // provider's subscription isn't torn down each render.
  const commands = useMemo<CommandSource>(
    () => ({
      onCommand: handler =>
        primaryTerminal
          ? primaryTerminal.onCommand(payload =>
              handler({
                cmd: payload.cmd,
                exit: payload.exit,
                cwd: payload.cwd,
              }),
            )
          : () => {},
    }),
    [primaryTerminal],
  )

  // Adapt the primary terminal's `mentor` + `mentor-pending` frames (#1148 /
  // #1566 — the mentor → lab → SPA disposition relay + the "AI looking…"
  // beat) into a course-kit MentorSource so `<ChapterProvider>` builds the
  // per-objective transcript / capstone / pending state the MentorDock
  // renders. Every disposition is forwarded verbatim (the reducer maps them);
  // keyed/memoized like the commands adapter above so the provider's
  // subscription isn't torn down each render.
  const mentor = useMemo<MentorSource>(
    () => ({
      onMentor: handler =>
        primaryTerminal
          ? primaryTerminal.onMentor(payload =>
              handler({
                objectiveId: payload.objectiveId,
                disposition: payload.disposition,
                ...(payload.rung !== undefined && { rung: payload.rung }),
                ...(payload.text !== undefined && { text: payload.text }),
              }),
            )
          : () => {},
      onPending: handler =>
        primaryTerminal
          ? primaryTerminal.onMentorPending(payload =>
              handler({ objectiveId: payload.objectiveId }),
            )
          : () => {},
    }),
    [primaryTerminal],
  )

  // Forward the reader's recovery signal (#1566) up to lab — a satisfying
  // command on an objective the learner struggled on. Lab marks capture
  // RECOVERED and, gated on the operator's encouragement setting, relays it
  // to the mentor for the success-after-struggle encouragement. No terminal
  // = no-op (a pure-content Part has no session).
  const onMentorRecovered = useCallback(
    (signal: { objectiveId: string; cmd: string }) => {
      primaryTerminal?.sendRecovered({
        objectiveId: signal.objectiveId,
        cmd: signal.cmd,
      })
    },
    [primaryTerminal],
  )

  // Adapt the primary terminal's probe round-trip (#1149 — browser
  // `probe` frame → lab bridge → hypervisor probe RPC → `probe-result`)
  // into a course-kit ProbeSource so `<ChapterProvider>`'s state-goal
  // scheduler can evaluate `{via:'state'}` detectors. No terminal =
  // never satisfied (pure-content Parts have no guest to probe).
  const probes = useMemo<ProbeSource>(
    () => ({
      runCheck: check =>
        primaryTerminal
          ? primaryTerminal.runCheck(check)
          : Promise.resolve({ satisfied: false }),
    }),
    [primaryTerminal],
  )

  // Reboot-survival (#1149): fire a reassert whenever the machine
  // (re)enters `running` on this connection — resume from `saved`,
  // reboot (`nixos-rebuild boot` + reboot drops the pty; the silent
  // reconnect above re-attaches), or a fresh attach. The provider then
  // re-probes every state detector with the rate limit bypassed, so
  // goals the reboot itself satisfied (a new generation active)
  // complete. `attach()` resets the cached lifecycle state to null, so
  // a post-reboot reconnect lands as null → running — which is why the
  // guard is "previous state on this connection wasn't running" rather
  // than "a non-running frame was observed first" (that would miss the
  // reboot case this seam exists for). The initial-attach fire is
  // deliberate: a returning learner's already-satisfied state goals
  // re-assert immediately.
  const reassert = useMemo<ReassertSource>(
    () => ({
      onReassert: handler => {
        if (!primaryTerminal) return () => {}
        let last = primaryTerminal.lifecycleState
        return primaryTerminal.onLifecycle(frame => {
          const prev = last
          last = frame.state
          if (frame.state === 'running' && prev !== 'running') handler()
        })
      },
    }),
    [primaryTerminal],
  )

  // Report the active objective/exercise focus up to lab (#1148) so
  // the bridge can arm the per-engagement mentor session with the
  // authored mentor-context. `primaryTerminal` is projected into React
  // state by a post-paint useEffect, so the provider — which latches
  // each objectiveId's focus and fires the first one during the initial
  // render — usually calls this before the terminal exists. A plain `?.`
  // forward would drop that first focus and never retry (the latch means
  // it fires once per objectiveId), leaving the chapter's first
  // objective with no mentor context. So remember the latest focus and
  // replay it once the terminal becomes available.
  const latestFocus = useRef<ChapterFocus | null>(null)
  const onFocus = useCallback(
    (focus: ChapterFocus) => {
      latestFocus.current = focus
      primaryTerminal?.sendFocus(focus)
    },
    [primaryTerminal],
  )
  useEffect(() => {
    if (primaryTerminal && latestFocus.current) {
      primaryTerminal.sendFocus(latestFocus.current)
    }
  }, [primaryTerminal])

  // Reader funnel — map the provider's analytics-agnostic transitions to
  // lab's umami events (#938). The provider stays React- and analytics-
  // free; lab owns the event vocabulary. `next_chapter` is a footer click,
  // wired separately below.
  const onFunnel = useCallback(
    (event: ReaderFunnelEvent) => {
      const base = {
        courseId: response.content.chapter.courseId,
        partId: response.content.chapter.partId,
        chapterId: response.content.chapter.chapterId,
      }
      switch (event.type) {
        case 'first-command':
          track('reader_first_command', base)
          break
        case 'objective-rethink':
          track('reader_objective_rethink', {
            ...base,
            objectiveId: event.objectiveId,
          })
          // #1150: report the Tier-1 firing to the mentor's flywheel
          // (retirement / re-validation signal) via the terminal WS.
          // Objectives only — exercise wrong turns have no funnel seam
          // today. Fire-and-forget; a closed socket drops it silently.
          primaryTerminal?.sendWrongTurn({
            objectiveId: event.objectiveId,
            wrongTurnId: event.wrongTurnId,
          })
          break
        case 'rethink-recovered':
          track('reader_rethink_recovered', {
            ...base,
            objectiveId: event.objectiveId,
          })
          break
        case 'chapter-complete':
          // Deliberate pair (#1218): the client `track()` beat is
          // blockable; the server-side POST fires the authoritative,
          // blocker-proof `progress_chapter_complete` from the lab daemon
          // with the learner UUID as the distinct id. The delta between
          // the two measures the content-blocker blind-rate. Fire-and-
          // forget — a failed completion beat must never break the reader.
          track('reader_chapter_complete', base)
          void app.account.completeChapter(base).catch(() => {})
          break
      }
    },
    [
      response.content.chapter.courseId,
      response.content.chapter.partId,
      response.content.chapter.chapterId,
      primaryTerminal,
      app,
    ],
  )

  // Persist each objective completion so a VM resume / reload restores the
  // chapter's checkmarks (#1493). Fire-and-forget on the account client,
  // same shape as the `completeChapter` beat in `onFunnel` — a failed
  // persist must never interrupt the reader.
  const onObjectiveComplete = useCallback(
    (objectiveId: string) => {
      void app.account
        .recordObjectiveComplete({
          courseId: response.content.chapter.courseId,
          partId: response.content.chapter.partId,
          chapterId: response.content.chapter.chapterId,
          objectiveId,
        })
        .catch(() => {})
    },
    [
      response.content.chapter.courseId,
      response.content.chapter.partId,
      response.content.chapter.chapterId,
      app,
    ],
  )

  // Module URL comes from the combined endpoint response (Part B of
  // #368) — content-hashed for cache-busting, served from lab disk.
  // The dynamic `import()` is browser-side; `@vite-ignore` tells
  // Vite's analyzer this is a runtime URL it shouldn't try to
  // bundle.
  const moduleUrl = response.content.chapter.module.url
  useEffect(() => {
    let active = true
    setModule(null)
    setModuleError(null)
    import(/* @vite-ignore */ moduleUrl)
      .then((mod: ChapterModule) => {
        if (active) setModule(() => mod.default)
      })
      .catch((err: unknown) => {
        // Don't navigate away — that strands the learner on /courses
        // with no feedback. Surface an actionable retry overlay so a
        // transient network blip is recoverable in place; a content-
        // hash mismatch after a republish is recoverable by reload.
        console.error('Failed to load chapter module', err)
        if (active) {
          setModuleError(err instanceof Error ? err.message : String(err))
        }
      })
    return () => {
      active = false
    }
  }, [moduleUrl, moduleReloadKey])

  // Revive the JSON-parsed spec through BOTH serde seams before the
  // runtime sees it — the two codecs target disjoint node shapes:
  //   - `reviveSpecWire` (course-kit/wire) turns every `$regex` wire
  //     node back into a live `RegExp` — the objective/exercise
  //     `Detector` families the builder serialized through
  //     `specWireReplacer` (#1201).
  //   - `QuizSerde.rehydrateChapterSpec` reconstructs each `__lcRegex`
  //     tag in the quiz bank's `accept` lists into a real `RegExp`
  //     (#1203 Part D). Idempotent, so a quiz-less spec is unchanged.
  // Both are needed: a chapter can carry regex detectors AND a quiz.
  const spec = useMemo(
    () =>
      QuizSerde.rehydrateChapterSpec(
        reviveSpecWire(response.content.chapter.spec) as ChapterSpec,
      ),
    [response.content.chapter.spec],
  )
  // Wire shape is `z.unknown()[]` by design — course-kit's `Term` is
  // the source of truth; the SPA asserts at the boundary (see
  // CourseHeaderSchema's JSDoc).
  const glossary = response.content.course.glossary as Term[]

  const prevHref = response.content.neighbors.prev
    ? `/courses/${response.content.neighbors.prev.courseId}/${response.content.neighbors.prev.partId}/${response.content.neighbors.prev.chapterId}`
    : null
  const nextHref = response.content.neighbors.next
    ? `/courses/${response.content.neighbors.next.courseId}/${response.content.neighbors.next.partId}/${response.content.neighbors.next.chapterId}`
    : null
  // Boundary fallback for the footer buttons: the first chapter's
  // Previous and the last chapter's Up-next wrap to the course landing
  // so no chapter dead-ends. The compact app-bar arrows keep the
  // null-omit behavior (they read as a chapter switcher, not a course
  // exit).
  const courseHref = `/courses/${response.content.chapter.courseId}`

  // A neighbor ref carries only ids; resolve each to its display title
  // from the parts listing so the footer reads the chapter title
  // ("Ephemeral Shells"), not the "02-ephemeral-shells" slug. Falls back
  // to the id if the listing somehow lacks the chapter.
  const neighborTitle = (
    ref: { partId: string; chapterId: string } | null,
  ): string | undefined =>
    ref
      ? response.content.parts
          .find(p => p.id === ref.partId)
          ?.chapters.find(c => c.id === ref.chapterId)?.title
      : undefined

  // App-bar chapter switcher label — the current chapter's position
  // WITHIN ITS PART ("Ch X / Y"), not the whole course. The prev/next
  // arrows beside it navigate within the Part (neighbors are Part-scoped;
  // see Courses.chapterNeighbors), so a course-wide count would read
  // "Ch 1 / 14" while the arrows only ever walk the Part's 7 — scope the
  // count to the current Part's chapters so label and arrows agree.
  // `content.parts` carries every Part's chapter listings; pick this one.
  // The full jump-to-chapter sheet (clicking the label) is deferred — the
  // switcher ships as the label + Part-scoped arrows over `neighbors`.
  const partChapters =
    response.content.parts.find(p => p.id === response.content.chapter.partId)
      ?.chapters ?? []
  // Match the switcher to the authored header eyebrows. Each chapter's eyebrow
  // numbers the Part as the learner sees it: the opening (chapter `number` 0) is
  // un-numbered ("First Contact · Orientation") and every later chapter is
  // "Chapter N" (= its 0-indexed `number`). So count only the numbered chapters
  // (skip the opening) and show the current chapter's own `number` — a flat
  // 1-based position over all chapters counts the opening as "Ch 1" and pushes
  // the closing chapter to "Ch 7 / 7" while its eyebrow reads "Chapter 6"
  // (#1676). The opening reuses its eyebrow's own chapter label ("Orientation")
  // rather than a number; `ChapterListingSchema` documents `number === 0` as the
  // opening.
  const numberedTotal = partChapters.filter(c => c.number >= 1).length
  const eyebrowLabel = spec.eyebrow.split('·').pop()?.trim()
  const chapterLabel =
    response.content.chapter.number >= 1 && numberedTotal > 0
      ? `Ch ${response.content.chapter.number} / ${numberedTotal}`
      : (eyebrowLabel ?? `Ch ${response.content.chapter.number}`)

  // Whether the primary VM is actively burning free-tier budget (in
  // `running`). Published to the chrome so the budget meter ticks its
  // countdown live only while budget is being consumed — frozen when the
  // VM is paused/idle/booting, and (via the `null` chrome) on non-reader
  // screens where there is no VM at all. Seeded from the cached lifecycle
  // state and kept current off the same `onLifecycle` feed the reassert
  // seam above uses.
  const [vmRunning, setVmRunning] = useState(false)
  useEffect(() => {
    if (!primaryTerminal) {
      setVmRunning(false)
      return
    }
    setVmRunning(primaryTerminal.lifecycleState === 'running')
    return primaryTerminal.onLifecycle(frame => {
      setVmRunning(frame.state === 'running')
    })
  }, [primaryTerminal])

  // Publish the reader-chrome bits up to the shell so the app bar (a
  // sibling above the routed outlet) renders the course label and
  // chapter switcher. Orientation is the live reader state — the
  // Settings "Default layout" control drives it through here, and
  // `<SplitLayout/>` below renders from the same `orientation`. Cleared
  // on unmount so the next screen shows the minimal bar. The effect
  // re-runs when the published values change (course/chapter nav,
  // orientation, VM running) so the bar stays current.
  const setReaderChrome = useSetReaderChrome()
  const courseTitle = response.content.course.title

  // Snapshot the reader's current context for the app-bar "Report an
  // issue" flow (#1672). Read live at submit time: the active objective
  // (`latestFocus`) and the VM lifecycle state (`primaryTerminal`) both
  // move after this closure is built, so the collector reads their
  // current values rather than closing over a render-time snapshot. The
  // lab daemon secret-redacts before any of this leaves the browser.
  const collectReportContext = useCallback((): ReportContext => {
    const chapter = response.content.chapter
    const objectiveId = latestFocus.current?.objectiveId
    const vmState = primaryTerminal?.lifecycleState ?? undefined
    return {
      where: `${courseTitle} · ${chapterLabel}`,
      courseId: chapter.courseId,
      partId: chapter.partId,
      chapterId: chapter.chapterId,
      ...(objectiveId !== undefined && { objectiveId }),
      contentHash: chapter.module.hash,
      ...(vmState !== undefined && { vmState }),
    }
  }, [response, courseTitle, chapterLabel, primaryTerminal])

  useEffect(() => {
    setReaderChrome({
      courseLabel: courseTitle,
      chapterLabel,
      orientation,
      setOrientation,
      // Umbrella #1357: the chapter completes on objectives with an
      // ungated footer — the knowledge-check gate moved to the Part
      // boundary — so the app-bar chapter switcher's next arrow is never
      // locked.
      chapterNav: { prevHref, nextHref, nextLocked: false },
      vmRunning,
      // Only offer "attach recent lab context" when a terminal is
      // actually present (#1672) — a machine-less chapter (e.g. a closing)
      // has no objective/VM/command context worth attaching, so the app
      // bar hides the toggle when this is absent.
      ...(primaryTerminal ? { collectReportContext } : {}),
    })
    return () => setReaderChrome(null)
  }, [
    setReaderChrome,
    courseTitle,
    chapterLabel,
    orientation,
    prevHref,
    nextHref,
    vmRunning,
    primaryTerminal,
    collectReportContext,
  ])

  // #1446 — reaching the closing means the Part's lab work is done. The
  // closing is machine-less (readerRecipe is empty, so no terminal renders
  // here), so release the engagement + shut the Part's VM eagerly instead
  // of leaving it for the idle-TTL reaper. Fires once when a closing mounts.
  useEffect(() => {
    if (spec.role === 'closing') {
      void app.chapter
        .leaveEngagement({ intent: 'terminate' })
        .catch(() => undefined)
    }
  }, [app, spec.role])

  // The next-chapter navigation the footer drives. Declared ABOVE the
  // machine-boot early returns below: these hooks must run on every render,
  // or the preparing→ready transition changes the hook count and React
  // throws "rendered more hooks than during the previous render" (Rules of
  // Hooks).
  const onNavigateNext = useCallback((): void => {
    if (!nextHref) return
    track('reader_next_chapter', {
      courseId: response.content.chapter.courseId,
      partId: response.content.chapter.partId,
      chapterId: response.content.chapter.chapterId,
    })
    navigate(nextHref)
  }, [
    nextHref,
    navigate,
    response.content.chapter.courseId,
    response.content.chapter.partId,
    response.content.chapter.chapterId,
  ])

  // The footer's single navigation entry point: the forward edge fires the
  // funnel event + advance (`onNavigateNext`); prev / boundary edges are a
  // plain router push. The footer itself refuses the forward edge while
  // gated, so this needn't re-check the lock.
  const onFooterNavigate = useCallback(
    (href: string): void => {
      if (href === nextHref) {
        onNavigateNext()
        return
      }
      // Leaving the lab entirely (the "Back to course" boundary) releases
      // the engagement + VMs promptly. Intra-chapter hops are NOT released
      // — that would cold-reboot the VM on every lesson turn; the
      // live-terminal conflict gate already makes an idle record harmless.
      if (href === courseHref) {
        void app.chapter
          .leaveEngagement({ intent: 'terminate' })
          .catch(() => undefined)
      }
      navigate(href)
    },
    [nextHref, onNavigateNext, navigate, courseHref, app],
  )

  // `primaryTerminal` is the first pool entry — the singleton
  // ChapterProvider hands to LabStep for command injection. The
  // useEffect above projects it into React state so this read sees
  // the post-attach pool. Multi-machine recipes render their
  // additional terminals as extra panes below; the LabStep API will
  // grow a `machineId` prop in the post-Part-D course-kit refactor.
  const firstMachineId = response.machines[0]?.machineId

  // First-render: the useEffect that primes the pool runs after paint,
  // so primaryTerminal is undefined here even when the chapter has
  // machines. Treat the "has-machines-but-pool-not-yet-primed" window
  // as still-loading — without this, the no-machines fallback below
  // fires and crashes (it renders <ChapterHeader> outside the
  // <ChapterProvider>, which throws inside useChapter()).
  if (firstMachineId && !primaryTerminal) return <PreparingLab />

  if (moduleError !== null) {
    return (
      <FatalOverlay
        message={moduleError}
        onRetry={() => setModuleReloadKey(k => k + 1)}
      />
    )
  }

  // A chapter whose spec lacks its objective list (stale or malformed
  // content — e.g. a pre-`objectives` build served by a stale course
  // host) would crash the objective runtime on mount: `<ChapterProgress>`
  // and the reducer both read `spec.objectives`. The wire type is
  // `z.unknown()` and the value reaches here through an `as ChapterSpec`
  // cast, so nothing validates it earlier — guard it explicitly and
  // surface a recoverable content error instead of white-screening.
  if (!Array.isArray(spec.objectives)) {
    return (
      <FatalOverlay
        message="This chapter’s content looks out of date and couldn’t be loaded. Reloading usually fixes it."
        onRetry={() => window.location.reload()}
      />
    )
  }

  // Footer nav links — the prev/next chapter, or a boundary "Back to course"
  // wrap at the ends so no chapter dead-ends (mirrors the app-bar switcher's
  // neighbor semantics). Built here so `ChapterBody` — which owns the gate —
  // can hand them straight to `<ChapterFooter/>`. Computed above the
  // machine-less branch so both reader paths hand the same footer down.
  const footerPrev: ChapterNavLink =
    prevHref && response.content.neighbors.prev
      ? {
          href: prevHref,
          title:
            neighborTitle(response.content.neighbors.prev) ??
            response.content.neighbors.prev.chapterId,
        }
      : { href: courseHref, title: courseTitle, dir: 'Back to' }
  // #1446 Part B — the last lesson of a role-tagged Part routes forward
  // to the Part assessment, not the next chapter. The server flags this
  // (`neighbors.nextIsAssessment`); the SPA builds the assessment URL.
  const assessmentHref = `/courses/${response.content.chapter.courseId}/${response.content.chapter.partId}/assessment`
  const footerNext: ChapterNavLink = response.content.neighbors.nextIsAssessment
    ? { href: assessmentHref, title: 'Part assessment' }
    : nextHref && response.content.neighbors.next
      ? {
          href: nextHref,
          title:
            neighborTitle(response.content.neighbors.next) ??
            response.content.neighbors.next.chapterId,
        }
      : { href: courseHref, title: courseTitle, dir: 'Back to' }

  if (!primaryTerminal) {
    // Pure-content chapter — an orientation `opening` or the `closing`,
    // objective-gated to no machines (umbrella #1446). Same reader shell as
    // the machine-ful path (ChapterProvider so `useChapter()` resolves;
    // ChapterBody so the footer, glossary, and closing feedback render), but
    // no terminal pane / SplitLayout — there is no VM here. (Before #1446 no
    // shipped chapter was machine-less, so this branch was a never-exercised
    // stub that rendered <ChapterHeader> outside the provider and crashed.)
    return (
      <ChapterProvider
        spec={spec}
        commands={commands}
        mentor={mentor}
        onRecovered={onMentorRecovered}
        probes={probes}
        reassert={reassert}
        onFocus={onFocus}
        onFunnel={onFunnel}
        onObjectiveComplete={onObjectiveComplete}
        completedObjectiveIds={response.completedObjectives}>
        <div className={styles.app}>
          <ChapterPane head={<ChapterHeader />}>
            <ChapterBody
              module={Module}
              glossary={glossary}
              footerPrev={footerPrev}
              footerNext={footerNext}
              onFooterNavigate={onFooterNavigate}
              glossaryEnabled={
                spec.role === 'lesson' || spec.role === 'closing'
              }
              onSubmitFeedback={
                spec.role === 'closing'
                  ? feedback =>
                      app.chapter.submitPartFeedback(
                        response.content.chapter.courseId,
                        response.content.chapter.partId,
                        feedback,
                      )
                  : undefined
              }
            />
          </ChapterPane>
        </div>
      </ChapterProvider>
    )
  }

  return (
    <ChapterProvider
      spec={spec}
      commands={commands}
      mentor={mentor}
      onRecovered={onMentorRecovered}
      probes={probes}
      reassert={reassert}
      onFocus={onFocus}
      onFunnel={onFunnel}
      onObjectiveComplete={onObjectiveComplete}
      completedObjectiveIds={response.completedObjectives}>
      <div className={styles.app}>
        <SplitLayout
          orientation={orientation}
          content={
            <ChapterPane
              head={
                <>
                  <ChapterProgress />
                  <ChapterHeader />
                </>
              }>
              <ChapterBody
                module={Module}
                glossary={glossary}
                footerPrev={footerPrev}
                footerNext={footerNext}
                onFooterNavigate={onFooterNavigate}
                glossaryEnabled={
                  spec.role === 'lesson' || spec.role === 'closing'
                }
                onSubmitFeedback={
                  spec.role === 'closing'
                    ? feedback =>
                        app.chapter.submitPartFeedback(
                          response.content.chapter.courseId,
                          response.content.chapter.partId,
                          feedback,
                        )
                    : undefined
                }
              />
            </ChapterPane>
          }
          terminal={
            <>
              <WorkspacePane
                footer={<MentorDock />}
                machines={response.machines.map(
                  (m): WorkspaceMachine => ({
                    machine: m,
                    terminal: app.chapter.terminalFor(m.machineId) ?? null,
                    envLabel: `${response.content.course.title} · ${m.machineId}`,
                    onPause: () => {
                      void app.chapter.suspendMachine(m.machineId)
                    },
                    onKeepAlive: () => {
                      void app.chapter.touchEngagement()
                    },
                    // #1060: wire the per-machine `terminating`-overlay
                    // "Start a fresh one" button (previously declared but
                    // never supplied, so it was hidden). Same page-level
                    // recovery the EvictionTakeover uses — re-post the
                    // engagement and refetch to re-attach the pool.
                    onExpiredRestart: () => {
                      void onExpiredRestart()
                    },
                  }),
                )}
              />
              {/* #1315: a spent free-tier cap takes precedence over the
                  resumable eviction surface — it's a snapshot-safe wall,
                  not a reconnect, so the calm out-of-time takeover (local
                  reset time + resume-reminder opt-in) replaces the "Wake it
                  up" dialog the budget-exhausted close would otherwise
                  (wrongly) show. `budgetExhausted` is the render trigger; the
                  takeover fetches its own reset time and push state. */}
              {budgetExhausted && <OutOfTimeTakeover />}
              {/* Eviction takeover scopes to the terminal pane — see
                  EvictionTakeover. Keyed off the primary machine's
                  eviction entry (#1056) so the copy matches the reason.
                  The SplitLayout `terminal` wrapper is `position:
                  relative`, so the overlay covers this pane only. */}
              {!budgetExhausted &&
                firstMachineId &&
                evictions.has(firstMachineId) &&
                !bootTrouble.has(firstMachineId) &&
                !failedMachineId && (
                  <EvictionTakeover
                    reason={evictions.get(firstMachineId) ?? null}
                    onResume={() => onResume(firstMachineId)}
                    onExpiredRestart={onExpiredRestart}
                  />
                )}
              {/* #1593: persistent hypervisor-hop boot failures (4004) replace
                  the resumable "dozed off" surface with an honest, capped
                  recovery so a server-side wedge can't loop "Wake it up"
                  forever. Below failedMachineId — a terminal fault wins. */}
              {!budgetExhausted &&
                firstMachineId &&
                bootTrouble.has(firstMachineId) &&
                !failedMachineId && (
                  <BootTroubleTakeover
                    onRetry={() => onBootRetry(firstMachineId)}
                  />
                )}
              {failedMachineId && (
                <FailedTakeover
                  onStartFresh={async () => {
                    try {
                      await app.chapter.restartMachine(failedMachineId)
                    } catch (err) {
                      console.error('restartMachine failed', err)
                    }
                    onRecovered()
                  }}
                />
              )}
            </>
          }
        />
      </div>
    </ChapterProvider>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Chapter body — single-column reader (prose + ungated footer)
////////////////////////////////////////////////////////////////////////////////

interface ChapterBodyProps {
  module: ComponentType | null
  glossary: Term[]
  footerPrev: ChapterNavLink | null
  footerNext: ChapterNavLink | null
  onFooterNavigate: (href: string) => void
  /**
   * Umbrella #1446 Part D — show the glossary affordance in the footer.
   * True only on `lesson`-role chapters; an opening/closing (and a
   * role-less pre-conformance chapter) pass `false`, so no button renders.
   */
  glossaryEnabled: boolean
  /**
   * Umbrella #1446 Part E — render the closing feedback affordance below
   * the prose. Supplied only on `closing`-role chapters (the objective-free
   * exit whose sole interaction is feedback); `undefined` elsewhere.
   */
  onSubmitFeedback?: (feedback: PartFeedbackRequest) => Promise<void>
}

/**
 * The scrolling reader body. Lives inside `<ChapterProvider/>` for the
 * objectives runtime (the `<Objective/>` cards read their state from it).
 *
 * Umbrella #1357 (the flip): the per-chapter knowledge-check quiz that used
 * to render inline below the prose and gate the next-chapter footer is
 * retired — mastery is now assessed once at the Part boundary (the
 * `PartAssessmentPage` at `/courses/:courseId/:partId/assessment`). So the
 * chapter completes on its objectives alone and the footer is **ungated**:
 * the MDX prose (with its `<Objective/>` cards) stays mounted, and the
 * `<ChapterFooter>` advances to the next chapter with no lock.
 *
 * @param props.module - the lazily-loaded MDX chapter component, or `null`
 *   while it loads
 * @param props.glossary - the course glossary for `<GlossaryProvider/>`
 * @param props.footerPrev - the footer's Previous link (or boundary wrap)
 * @param props.footerNext - the footer's Next link (or boundary wrap)
 * @param props.onFooterNavigate - the footer's navigation callback
 * @returns the reader body
 */
function ChapterBody(props: ChapterBodyProps): JSX.Element {
  const {
    module: Module,
    glossary,
    footerPrev,
    footerNext,
    onFooterNavigate,
    glossaryEnabled,
    onSubmitFeedback,
  } = props

  // #1446 Part D — the course-wide glossary opens as an overlay drawer
  // from the lesson footer button, keeping the learner in the Part flow
  // (no route change, no per-lesson fork of the data).
  const [glossaryOpen, setGlossaryOpen] = useState(false)

  return (
    <>
      <GlossaryProvider terms={glossary}>
        <MdxProvider>
          <Suspense fallback={null}>{Module ? <Module /> : null}</Suspense>
        </MdxProvider>
      </GlossaryProvider>
      {onSubmitFeedback && <ClosingFeedback onSubmit={onSubmitFeedback} />}
      <ChapterFooter
        prev={footerPrev}
        next={footerNext}
        onNavigate={onFooterNavigate}
        onGlossary={glossaryEnabled ? () => setGlossaryOpen(true) : undefined}
      />
      {glossaryOpen && (
        <div
          className={styles.glossaryScrim}
          role="dialog"
          aria-modal
          aria-label="Course glossary"
          data-testid="glossary-drawer"
          onClick={() => setGlossaryOpen(false)}>
          <div
            className={styles.glossaryDrawer}
            onClick={e => e.stopPropagation()}>
            <div className={styles.glossaryDrawerHead}>
              <h2 className={styles.glossaryDrawerTitle}>Glossary</h2>
              <button
                type="button"
                className={styles.glossaryClose}
                data-testid="glossary-close"
                onClick={() => setGlossaryOpen(false)}
                aria-label="Close glossary">
                ✕
              </button>
            </div>
            <div className={styles.glossaryDrawerBody}>
              <Glossary terms={glossary} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Conflict dialog — "you have this course open elsewhere"
////////////////////////////////////////////////////////////////////////////////

interface ConflictDialogProps {
  current: Extract<ChapterResponse, { kind: 'conflict' }>['current']
  onContinue: () => void
  onStartFresh: () => void | Promise<void>
}

function ConflictDialog(props: ConflictDialogProps): JSX.Element {
  const { current, onContinue, onStartFresh } = props
  const minutesAgo = Math.max(
    1,
    Math.round((Date.now() - current.startedAt) / 60_000),
  )
  return (
    <div className={styles.scrim} role="alertdialog" aria-modal>
      <div className={styles.sheet}>
        <span className={styles.sheetEyebrow}>Engaged elsewhere</span>
        <h2 className={styles.sheetTitle}>Your lab’s live in another window</h2>
        <p className={styles.sheetBody}>
          You have a live terminal open on <strong>{current.partTitle}</strong>{' '}
          → <strong>{current.chapterTitle}</strong>, started {minutesAgo}{' '}
          {minutesAgo === 1 ? 'minute' : 'minutes'} ago ({current.machineCount}{' '}
          {current.machineCount === 1 ? 'machine' : 'machines'} running). Two
          windows can’t drive one machine — jump back to it, or bring it here
          and close the other.
        </p>
        <div className={styles.sheetRow}>
          {/* Safe action is primary + autofocus: jump to the running tab.
              Taking over here closes the existing session, so it's the
              non-default choice. */}
          <button
            className={`${styles.edgeBtn} ${styles.edgeBtnPrimary}`}
            type="button"
            onClick={onContinue}
            autoFocus>
            Keep it there
          </button>
          <button
            className={styles.edgeBtn}
            type="button"
            onClick={() => void onStartFresh()}>
            Bring it here — close the other
          </button>
        </div>
      </div>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Eviction takeover — fired when a pooled WS closes mid-chapter
////////////////////////////////////////////////////////////////////////////////

interface EvictionTakeoverProps {
  reason: MachineLifecycleReason | null
  onResume: () => void
  onExpiredRestart: () => void | Promise<void>
}

/**
 * Copy + recovery for each eviction `reason` (#1056). `engagement-expired`
 * is the one case whose recovery isn't "wake the snapshot" — the
 * engagement lapsed, so a fresh one is minted via `onExpiredRestart`.
 */
function evictionCopy(reason: MachineLifecycleReason | null): {
  eyebrow: string
  title: string
  body: string
  cta: string
  expired: boolean
} {
  switch (reason) {
    case 'engagement-expired':
      return {
        eyebrow: 'Session ended',
        title: 'Your previous lab expired',
        body: 'This lab session timed out. Start a fresh shell to keep going — your chapter progress is saved.',
        cta: 'Start a fresh shell',
        expired: true,
      }
    case 'voluntary-pause':
      return {
        eyebrow: 'Paused',
        title: 'You paused your lab',
        body: 'Your work is snapshotted and safe. Resume to pick up exactly where you left it.',
        cta: 'Resume',
        expired: false,
      }
    case 'operator-suspend':
      return {
        eyebrow: 'Paused',
        title: 'An operator paused your lab',
        body: 'An operator paused this session to free capacity. Your work is snapshotted and safe — resume to pick up exactly where you left it.',
        cta: 'Resume',
        expired: false,
      }
    case 'crash':
      return {
        eyebrow: 'Reconnect',
        title: 'Your shell stopped unexpectedly',
        body: 'The shell process exited. Resume to bring it back — your chapter progress is saved.',
        cta: 'Resume',
        expired: false,
      }
    // A spent free-tier cap never reaches here: onClose routes the 4403
    // BUDGET_EXHAUSTED close to the out-of-time takeover before the eviction
    // surface (#1315). That routing keys on the close CODE, not this
    // lifecycle reason — the reason is `null` on the re-open budget close, so
    // a reason-based branch would miss it and fall through to "dozed off".
    case 'idle-suspend':
    case 'daemon-restart':
    default:
      return {
        eyebrow: 'Reconnect',
        title: 'Your shell dozed off',
        body: 'The tab slept or the network flickered — your work is snapshotted and safe. Pick up exactly where you left it.',
        cta: 'Wake it up',
        expired: false,
      }
  }
}

/**
 * Pane takeover shown when the shell connection drops mid-chapter. The
 * copy + recovery adapt to the lifecycle `reason` (#1056): an idle /
 * disconnect resume wakes the snapshot; an `engagement-expired` mints a
 * fresh engagement. Scoped to the terminal/workspace pane — the shell is
 * what dropped, so it covers only that pane and leaves the chapter prose
 * readable. The deep-navy dim + chrome vocabulary mirror
 * `terminal_overlay`.
 */
function EvictionTakeover(props: EvictionTakeoverProps): JSX.Element {
  const copy = evictionCopy(props.reason)
  return (
    <div className={styles.paneTakeover} role="alertdialog" aria-modal>
      <span className={styles.paneTakeoverEyebrow}>{copy.eyebrow}</span>
      <h2 className={styles.paneTakeoverTitle}>{copy.title}</h2>
      <p className={styles.paneTakeoverBody}>{copy.body}</p>
      <button
        className={styles.paneTakeoverBtn}
        type="button"
        onClick={() => {
          if (copy.expired) void props.onExpiredRestart()
          else props.onResume()
        }}
        autoFocus>
        {copy.cta}
      </button>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Failed takeover — a terminally-failed shell (#1009), not resumable
////////////////////////////////////////////////////////////////////////////////

interface FailedTakeoverProps {
  onStartFresh: () => void | Promise<void>
}

/**
 * Pane takeover shown when a shell hits a terminal fault it can't be
 * resumed from — a snapshot write that hung past the hypervisor watchdog
 * (#1009). Distinct from {@link EvictionTakeover}: there's nothing to
 * wake up, so the only recovery is to terminate the stuck machine and
 * cold-boot a clean one. Same pane-scoped chrome vocabulary; the copy
 * is honest that this isn't a snapshot-safe resume.
 */
function FailedTakeover(props: FailedTakeoverProps): JSX.Element {
  return (
    <div className={styles.paneTakeover} role="alertdialog" aria-modal>
      <span className={styles.paneTakeoverEyebrow}>Shell trouble</span>
      <h2 className={styles.paneTakeoverTitle}>This shell got stuck</h2>
      <p className={styles.paneTakeoverBody}>
        Your shell hit a snag and couldn’t be recovered. Start a fresh one to
        keep going — it replaces the stuck machine with a clean shell.
      </p>
      <button
        className={styles.paneTakeoverBtn}
        type="button"
        onClick={() => void props.onStartFresh()}
        autoFocus>
        Start a fresh shell
      </button>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Boot-trouble takeover — persistent hypervisor-hop boot failure (#1593)
////////////////////////////////////////////////////////////////////////////////

interface BootTroubleTakeoverProps {
  onRetry: () => void
}

/**
 * Pane takeover shown when a shell repeatedly fails to start at the hypervisor
 * hop (`UPSTREAM_FAILED` / 4004) — a persistent server-side boot failure, not a
 * snapshot-safe blip. Distinct from {@link EvictionTakeover}'s "dozed off /
 * Wake it up": that copy implies a transient hiccup and, on a real server-side
 * wedge (the #1593 cgroup incident), loops forever. This surface is honest that
 * the fault is on our end, bounds the loop, and offers a plain retry. Same
 * pane-scoped chrome vocabulary.
 */
function BootTroubleTakeover(props: BootTroubleTakeoverProps): JSX.Element {
  return (
    <div className={styles.paneTakeover} role="alertdialog" aria-modal>
      <span className={styles.paneTakeoverEyebrow}>
        {BOOT_TROUBLE_COPY.eyebrow}
      </span>
      <h2 className={styles.paneTakeoverTitle}>{BOOT_TROUBLE_COPY.title}</h2>
      <p className={styles.paneTakeoverBody}>{BOOT_TROUBLE_COPY.body}</p>
      <button
        className={styles.paneTakeoverBtn}
        type="button"
        onClick={props.onRetry}
        autoFocus>
        {BOOT_TROUBLE_COPY.cta}
      </button>
    </div>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Loading + fatal overlays
////////////////////////////////////////////////////////////////////////////////

function PreparingLab(): JSX.Element {
  return (
    <div className={styles.fatal} role="status" aria-live="polite">
      <div className={styles.fatalCard}>
        <span className={styles.fatalEyebrow}>Loading</span>
        <h2 className={styles.fatalTitle}>Preparing your lab…</h2>
        <p className={styles.fatalBody}>One moment.</p>
      </div>
    </div>
  )
}

interface FatalOverlayProps {
  message: string
  onRetry: () => void
}

function FatalOverlay(props: FatalOverlayProps): JSX.Element {
  return (
    <div className={styles.fatal} role="alertdialog" aria-modal>
      <div className={styles.fatalCard}>
        <span className={styles.fatalEyebrow}>Failed to load</span>
        <h2 className={styles.fatalTitle}>Something went wrong.</h2>
        <p className={styles.fatalBody}>{props.message}</p>
        <button
          className={styles.fatalBtn}
          type="button"
          onClick={props.onRetry}
          autoFocus>
          Try again
        </button>
      </div>
    </div>
  )
}
